import { useEffect, useRef, useCallback } from 'react';

/**
 * useDebouncedSetter — Round 92o++++++++N++++++++++ (May 2026).
 *
 * Returns a debounced version of `setter`. Each call clears the
 * previous pending invocation and schedules a fresh one `delayMs`
 * later. If the user keeps calling within `delayMs`, the setter is
 * never actually invoked until typing stops.
 *
 * User mandate verbatim: "add a timer after each letter, 1.5
 * seconds; the timer resets every time a letter is put into the
 * form. The search fires after 1.5 seconds, no matter what, if no
 * letters have been typed before the timer ends naturally."
 *
 * Critically this does NOT touch the search logic — callers keep
 * their existing `setSearchQuery` filtering pipeline. We just delay
 * WHEN the setter fires. Typing feedback stays instant because the
 * caller drives a separate local-input state for the field's
 * `value` prop; only the downstream `searchQuery` state is
 * debounced.
 *
 * @param {Function} setter   — the state setter to debounce
 *                              (typically `setSearchQuery`).
 * @param {number}   delayMs  — wait time after last call.
 * @returns {Function}        — call with the value you want set.
 *
 * The returned function also exposes:
 *   • `.flush(value)`  — fire immediately with this value, cancel
 *                        the pending timer (used for Clear-Search
 *                        buttons where waiting 1.5s would feel
 *                        sluggish).
 *   • `.cancel()`      — drop any pending invocation.
 */
export default function useDebouncedSetter(setter, delayMs = 1500) {
  const timerRef = useRef(null);
  const setterRef = useRef(setter);
  // Track the latest setter ref so callers don't have to wrap their
  // setState in useCallback. setState from useState is stable but
  // setters that come from props or context might not be.
  useEffect(() => { setterRef.current = setter; }, [setter]);
  // Clean up the pending timer when the component unmounts so we
  // never fire a setState into an unmounted tree.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const debounced = useCallback((value) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try { setterRef.current(value); } catch { /* ignore */ }
      timerRef.current = null;
    }, delayMs);
  }, [delayMs]);

  debounced.flush = (value) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    try { setterRef.current(value); } catch { /* ignore */ }
  };
  debounced.cancel = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };
  return debounced;
}
